import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, User, Lock, Smartphone, Shield, Moon, Sun, Bell, ChevronRight } from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [biometricEnabled, setBiometricEnabled] = useState(user?.biometric_enabled || false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.two_factor_enabled || false);

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8 lg:max-w-3xl lg:mx-auto">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>
        </div>
        
        <div className="px-6 lg:px-0 lg:grid lg:grid-cols-2 lg:gap-8 space-y-6 lg:space-y-0">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Profile */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Profile</h2>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <div className="p-4 flex items-center gap-4">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt={user.full_name} className="w-14 h-14 rounded-full object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-xl font-bold">{user?.full_name?.charAt(0) || 'U'}</div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{user?.full_name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <p className="text-sm text-muted-foreground">{user?.phone}</p>
                  </div>
                  <button className="p-2 hover:bg-white/5 rounded-lg transition-colors"><User className="w-5 h-5 text-muted-foreground" /></button>
                </div>
              </div>
            </div>

            {/* Security */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Security</h2>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => toast.info('Change PIN coming soon')} className="w-full flex items-center gap-4 p-4 border-b border-white/10 hover:bg-white/5 transition-colors">
                  <Lock className="w-5 h-5 text-muted-foreground" /><span className="flex-1 text-left">Change PIN</span><ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
                <div className="flex items-center gap-4 p-4 border-b border-white/10">
                  <Smartphone className="w-5 h-5 text-muted-foreground" /><span className="flex-1">Biometric Login</span>
                  <Switch checked={biometricEnabled} onCheckedChange={(v) => { setBiometricEnabled(v); toast.success(v ? 'Biometric login enabled' : 'Biometric login disabled'); }} data-testid="biometric-toggle" />
                </div>
                <div className="flex items-center gap-4 p-4">
                  <Shield className="w-5 h-5 text-muted-foreground" /><span className="flex-1">Two-Factor Auth</span>
                  <Switch checked={twoFactorEnabled} onCheckedChange={(v) => { if (v) { toast.info('2FA setup coming soon'); } else { setTwoFactorEnabled(false); } }} data-testid="2fa-toggle" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Preferences */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Preferences</h2>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-4 p-4 border-b border-white/10">
                  {theme === 'dark' ? <Moon className="w-5 h-5 text-muted-foreground" /> : <Sun className="w-5 h-5 text-muted-foreground" />}
                  <span className="flex-1">Dark Mode</span>
                  <Switch checked={theme === 'dark'} onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')} data-testid="theme-toggle" />
                </div>
                <button onClick={() => navigate('/notifications')} className="w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
                  <Bell className="w-5 h-5 text-muted-foreground" /><span className="flex-1 text-left">Notification Preferences</span><ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Account Info */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Account</h2>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <span className="text-muted-foreground">LekeeWallet ID</span><span className="font-mono text-sm">{user?.lekeewallet_id}</span>
                </div>
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <span className="text-muted-foreground">User Type</span><span className="capitalize">{user?.user_type || 'Individual'}</span>
                </div>
                <div className="flex items-center justify-between p-4">
                  <span className="text-muted-foreground">KYC Tier</span><span>Tier {user?.kyc_tier || 1}</span>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Danger Zone</h2>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => toast.info('Account closure coming soon')} className="w-full flex items-center gap-4 p-4 text-red-500 hover:bg-white/5 transition-colors">
                  <span className="flex-1 text-left">Close Account</span><ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">This will permanently close your LekeeWallet account</p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
